import { Button, CornerIconButton, IconButton } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { FetchableList, InfiniteFetchableList } from "@/src/components/fetchableLists";
import { IconTab } from "@/src/components/iconTab";
import { MinMaxPicker } from "@/src/components/minMaxPicker";
import { MySelect } from "@/src/components/mySelect";
import PageHeader from "@/src/components/pageHeader";
import { ReorderableItems } from "@/src/components/reorderable";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import { Input } from "@/src/components/universalInput";
// import { VariableMinMaxPicker } from "@/src/components/variableMinMaxPicker";
import {
  FieldsSection,
  InputsType,
  Patient,
  PatientDocument,
  Reference,
  ReferenceCategory,
  StatisticsFilter,
  StatisticsFilterType,
  StatisticsReport,
  StatisticsReportType,
  StatisticsValueType,
  appointmentStatuses,
  assuranceAffiliations,
  patientColumnsNames,
} from "@/src/data/allTypes";
import { communes } from "@/src/data/communes";
import { nationalities } from "@/src/data/nationalities";
import { wilayas } from "@/src/data/wilayas";
import apiFetch from "@/src/utils/apiFetch";
import {
  getRemedType,
  getUserId,
  getUserShortName,
  jsonAbstract,
  uid,
} from "@/src/utils/utils";
import moment from "moment";
import React, { useContext, useState } from "react";
import { HiOutlineVariable } from "react-icons/hi";
import { IoMdAdd } from "react-icons/io";
import { openDocumentForm } from "../documents/documentsHook";
import { VariableInputPicker } from "../documents/variableInputPicker";
import {
  deepReplaceVariables,
  replaceVariablesWithValues,
} from "../documents/variablesHook";
import { openSnomedConceptPicker } from "../snomed/SnomedConceptPicker";
import StatisticsReportDrawer from "./statisticsReportDrawer";

export type VariableSectionsContextData = {
  sections: (FieldsSection | null | undefined)[] | null | undefined;
};

export const VariableSectionsContext = React.createContext<
  VariableSectionsContextData | undefined
>(undefined);

export function VariableSectionsProvider({
  value,
  children,
}: {
  value: VariableSectionsContextData;
  children: any;
}) {
  return (
    <VariableSectionsContext.Provider value={value}>
      {children}
    </VariableSectionsContext.Provider>
  );
}

function StatisticsPage() {
  return (
    <div className="page p20">
      <div className="c1 gap25">
        <PageHeader
          title="Rapports statistiques"
          subTitle="Générez des rapports statistiques détaillés"
        ></PageHeader>

        <StatisticsHistory></StatisticsHistory>
      </div>
    </div>
  );
}

function StatisticsHistory() {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState<string>("");

  async function openFiltersDrawer({
    report,
  }: {
    report: StatisticsReport | null | undefined;
  }) {
    if (!report) return;
    await openDrawer({
      id: "statisticsFiltersDrawer",
      title: "Filtres",
      additionalClasses: ["big"],
      trailing: (
        <div>
          <Input
            name={"Nom du rapport"}
            type="text"
            value={report.title || ""}
            onChange={(newValue) => {
              report.title = newValue;
            }}
          ></Input>
        </div>
      ),
      content: (
        <div className="c1 gap10">
          <FiltersPicker
            variableSections={[]}
            report={report}
            current={report.groups}
            onChange={(newValue) => {
              if (!report) return;
              report.groups = newValue;
            }}
          ></FiltersPicker>
        </div>
      ),

      onConfirm: async () => {
        console.log("report", report);
        let res = await apiFetch("statistics", "calculateStatisticsReport", {
          report: report!,
        });

        await openReportDrawer({ report: res! });
        setReload((prev) => prev + 1);
        return false;
      },
    });
  }

  async function openReportDrawer({ report }: { report: StatisticsReport }) {
    await openDrawer({
      id: "statisticsReportsDrawer",
      title: "Résultats",
      additionalClasses: ["medium"],
      content: <StatisticsReportDrawer data={report} />,
      footer: <div></div>,
    });
  }

  return (
    <CompartmentCard
      title="Historique des Rapports statistiques"
      color="red"
      buttonTitle="Nouveau rapport"
      onClick={async () => {
        let report: StatisticsReport = {
          males: 0,
          females: 0,
          patients: [],
          patientsDates: {},
          appointments: [],
          appointmentsDates: {},
          patientGroups: {},
          type: "clinicalData",
          creator: getUserId(),
          groups: [],
        };

        openFiltersDrawer({ report });
      }}
    >
      <InfiniteFetchableList
        queryKey={["StatisticsHistory", searchKey, reload]}
        getFunction={(limit, offset, searchKey) =>
          apiFetch("statistics", "loadStatisticsReports", {
            searchKey: searchKey,
            userId: getUserId(),
            limit,
            offset,
          })
        }
        emptyChild={
          <div className="center gray p20">
            <div>Aucun rapport trouvé</div>
          </div>
        }
      >
        {(data) => (
          <div className="c1 gap10">
            {data?.map((report) => {
              return (
                <Card
                  key={uid()}
                  color="red"
                  title={
                    report.title ||
                    (report?.type == "clinicalData"
                      ? "Rapport sur Données Cliniques"
                      : "Rapport standard")
                  }
                  subTitle={getUserShortName(report.creator)}
                  onClick={async () => {
                    await openReportDrawer({ report });
                  }}
                  onEdit={() => {
                    openFiltersDrawer({ report });
                  }}
                  onDelete={async () => {
                    await apiFetch("statistics", "deleteStatisticsReport", {
                      id: report.id,
                    });
                    setReload((prev) => prev + 1);
                  }}
                >
                  <div className="gray">
                    {moment(report._date).format("LL HH:mm")}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </InfiniteFetchableList>
    </CompartmentCard>
  );
}

function StatisticsTypePicker({
  onChange,
}: {
  onChange: (newValue: StatisticsReportType) => void;
}) {
  const [type, setType] = useState<StatisticsReportType>("clinicalData");
  const tabs: { id: StatisticsReportType; name: string }[] = [
    {
      id: "clinicalData",
      name: `Données cliniques`,
    },

    {
      id: "standardReport",
      name: `Rapports standards`,
    },
  ];

  return (
    <div className="grid c2 gap10">
      {tabs.map((tab) => {
        return (
          <IconTab
            key={uid()}
            activeTabId={type}
            id={tab.id}
            name={tab.name}
            // icon={< FaRegHospital/>}
            onChange={() => {
              setType(tab.id);
              onChange(tab.id);
            }}
          ></IconTab>
        );
      })}
    </div>
  );
}

function ConditionTypePicker({
  onChange,
}: {
  onChange: (
    newValue:
      | "patient"
      | "formulaires"
      | "motives"
      | "diagnostics"
      | "antecedents"
      | "appointment"
      | "snomed"
  ) => void;
}) {
  let tabs: {
    id:
      | "patient"
      | "formulaires"
      | "motives"
      | "diagnostics"
      | "antecedents"
      | "appointment"
      | "snomed";
    name: string;
  }[] = [
    {
      id: "patient",
      name: `Etat civil`,
    },

    {
      id: "snomed",
      name: `Filtres généraux`,
    },

    {
      id: "antecedents",
      name: `Antécédents`,
    },

    {
      id: "motives",
      name: `Motifs`,
    },

    {
      id: "diagnostics",
      name: `Diagnostics`,
    },

    {
      id: "formulaires",
      name: `Formulaires`,
    },
    // {
    //   id: "appointment",
    //   name: `Rendez-vous`,
    // },
  ];

  if (getRemedType() == "FORENXPERT")
    tabs = tabs.filter(
      (tab) => !["appointment", "diagnostics", "motives"].includes(tab.id)
    );

  return (
    <div className="c1 gap10">
      {tabs.map((tab) => {
        return (
          <IconTab
            key={uid()}
            activeTabId={null}
            id={tab.id}
            name={tab.name}
            // icon={< FaRegHospital/>}
            onChange={() => {
              onChange(tab.id);
            }}
          ></IconTab>
        );
      })}
    </div>
  );
}

//! //////////////////////////////////////////////////////////////////
//! /////////////////     FILTERS   ///////////////////////////
//! /////////////////////////////////////////////////////////////////////

function FiltersPicker({
  report,
  current,
  variableSections,
  onChange,
}: {
  report: StatisticsReport;
  current: StatisticsFilter[][];
  variableSections: (FieldsSection | null | undefined)[] | null | undefined;
  onChange?: (newValue: StatisticsFilter[][]) => void;
}) {
  const [reload, setReload] = useState(0);
  const [groups, setGroups] = useState(current);

  function onGroupsChange(newGroups: StatisticsFilter[][]) {
    setGroups([...newGroups]);
    onChange?.([...newGroups]);
  }

  async function openForm({
    index,
    filterName,
    filterType,
    categoriesIds,
  }: {
    index: number;
    filterName?: string;
    filterType: StatisticsFilterType;
    categoriesIds: ReferenceCategory[];
  }) {
    let filters = groups[index] || [];

    function onConfirm(filters: StatisticsFilter[]) {
      if (index == -1) groups.push(filters);
      else groups[index] = filters;
      onGroupsChange(groups);
      closeDrawer("conditionTypeDrawer");
      setReload(reload + 1);
    }

    if (filterType == "refAnswer" && filterName == "snomed") {
      return await openSnomedConceptPicker({
        title: "Filtrer par concept Snomed",
        category: "motive",
        onSelect: (reference) => {
          console.log("reference", reference);
          filters.push({
            table: "refAnswers",
            columnName: "reference.id",
            localId: reference.id,
            visibleName: reference.name || "Référence",
            visibleValue: " ",
            valueType: "select",
            value: reference.id,
            subFilters: [],
            creator: getUserId(),
          });

          onConfirm(filters);
        },
      });
    }

    if (filterType == "refAnswer") {
      return await openDrawer({
        id: "formDrawer",
        title: `Filtrer par ${filterName} `,
        additionalClasses: ["medium"],
        content: (
          <div className="c1 gap10">
            <VariableSectionsProvider value={{ sections: variableSections }}>
              <RefAnswerPicker
                current={filters}
                categoriesIds={categoriesIds}
                onChange={(newFilters) => {
                  filters = newFilters;
                  closeDrawer("formDrawer");
                  onConfirm(filters);
                }}
              />
            </VariableSectionsProvider>
          </div>
        ),
        footer: <div></div>,
      });
    }

    await openDrawer({
      id: "formDrawer",
      title: `Filtrer par ${filterName} `,
      additionalClasses: ["medium"],

      content: (
        <div className="c1 gap10" style={{ minWidth: "40vw" }}>
          <VariableSectionsProvider value={{ sections: variableSections }}>
            {filterType == "patient" && (
              <StatisticsPatientIdentityForm
                current={filters}
                onChange={(newValue) => {
                  filters = newValue;
                }}
              />
            )}

            {filterType == "appointment" && (
              <StatisticsAppointmentPicker
                current={filters}
                onChange={(newValue) => {
                  filters = newValue;
                }}
              />
            )}
          </VariableSectionsProvider>
        </div>
      ),
      onConfirm: async () => {
        onConfirm(filters);
      },
    });
  }

  async function openConditionTypeDrawer({ index }: { index: number }) {
    openDrawer({
      id: "conditionTypeDrawer",
      title: "Ajouter un filtre par",
      content: (
        <ConditionTypePicker
          onChange={async (newValue) => {
            if (newValue == "patient")
              await openForm({
                index,
                filterName: "Etat civil",
                filterType: "patient",
                categoriesIds: [],
              });

            if (newValue == "appointment")
              await openForm({
                index,
                filterName: "Rendez-vous",
                filterType: "appointment",
                categoriesIds: [],
              });

            if (newValue == "motives")
              await openForm({
                index,
                filterName: "Motifs",
                filterType: "refAnswer",
                categoriesIds: ["motive"],
              });

            if (newValue == "formulaires")
              await openForm({
                index,
                filterName: "Formulaires",
                filterType: "refAnswer",
                categoriesIds: ["formulaire"],
              });

            if (newValue == "diagnostics")
              await openForm({
                index,
                filterName: "Diagnostics",
                filterType: "refAnswer",
                categoriesIds: ["diagnostic"],
              });

            if (newValue == "antecedents")
              await openForm({
                index,
                filterName: "Antécédents",
                filterType: "refAnswer",
                categoriesIds: [
                  "personalAntecedent",
                  "surgicalAntecedent",
                  "riskFactor",
                  "familialAntecedent",
                ],
              });
            if (newValue == "snomed")
              await openForm({
                index,
                filterName: "snomed",
                filterType: "refAnswer",
                categoriesIds: [],
              });
          }}
        ></ConditionTypePicker>
      ),
      footer: <div></div>,
    });
  }

  return (
    <VariableSectionsProvider value={{ sections: variableSections }}>
      <div className="c1 gap20" style={{ minWidth: "60vw" }}>
        {getRemedType() == "FORENXPERT" ? null : (
          <StatisticsTypePicker
            onChange={async (newType) => {
              report.type = newType;
              setGroups([...groups]);
            }}
          ></StatisticsTypePicker>
        )}
        {report.type == "standardReport" && (
          <StatisticsDocumentsPicker></StatisticsDocumentsPicker>
        )}
        {report.type == "clinicalData" ? (
          <FetchableList
            queryFn={() => groups}
            queryKey={["FiltersPicker", reload]}
            emptyChild={
              <div className="center p30 gap20 grid c1">
                <div className="gray  center">Aucun filtre ajouté</div>
                <Button
                  onClick={async () => {
                    await openConditionTypeDrawer({ index: -1 });
                  }}
                >
                  Créer un groupe de filtres
                </Button>
              </div>
            }
          >
            {(groups) => (
              <div key={uid()} className="c3 mc1 gap10">
                {groups?.map((group, index) => {
                  return (
                    <div key={uid()} className="grid cauto-1 gap10">
                      <div className="gray center">
                        {" "}
                        {index != 0 ? "Ou" : null}
                      </div>
                      <CompartmentCard
                        title={`Groupe ${index + 1}`}
                        onClick={async () => {
                          await openConditionTypeDrawer({ index });
                        }}
                      >
                        <ReorderableItems
                          listId={`filters ${index}`}
                          listType={"FILTER"}
                          items={group}
                          onReorder={(
                            newItems,
                            oldIndex,
                            newIndex,
                            movedItem
                          ) => {
                            let newGroup = [...group];
                            newGroup.splice(newIndex, 0, movedItem);
                            groups[index] = newGroup;

                            onGroupsChange(groups);
                          }}
                          onRemove={(item, index) => {
                            group.splice(index, 1);
                            if (group.length == 0) groups.splice(index, 1);
                            onGroupsChange(groups);
                          }}
                        >
                          {group?.map((filter, index) => {
                            return (
                              <div key={uid()}>
                                {index != 0 ? (
                                  <div className="gray center p5 mtop5">Et</div>
                                ) : null}
                                {
                                  <FilterCard
                                    variableSections={variableSections}
                                    filter={filter}
                                    level={0}
                                    onDelete={() => {
                                      group.splice(index, 1);
                                      if (group.length == 0)
                                        groups.splice(index, 1);
                                      onGroupsChange(groups);
                                    }}
                                    onChange={(newFilter) => {
                                      group[index] = newFilter;
                                      onGroupsChange(groups);
                                    }}
                                    onClick={async () => {
                                      console.log("filter", filter);
                                      if (filter.table == "patients") {
                                        await openForm({
                                          index,
                                          filterName: "Etat civil",
                                          filterType: "patient",
                                          categoriesIds: [],
                                        });
                                      }
                                    }}
                                  ></FilterCard>
                                }
                              </div>
                            );
                          })}
                        </ReorderableItems>
                      </CompartmentCard>
                    </div>
                  );
                })}
                <div className="mleft15 grid c1" style={{ height: "100%" }}>
                  <Button
                    type={"tertiary"}
                    onClick={async () => {
                      await openConditionTypeDrawer({ index: -1 });
                    }}
                  >
                    <div className="c1  center">
                      <IoMdAdd size={30} />
                      <div>Ajouter un groupe</div>
                    </div>
                  </Button>
                </div>
              </div>
            )}
          </FetchableList>
        ) : null}
      </div>
    </VariableSectionsProvider>
  );
}

function StatisticsDocumentsPicker({}: {}) {
  const [searchKey, setSearchKey] = useState<string>("");

  return (
    <FetchableList
      queryKey={["StatisticsDocumentsPicker", searchKey]}
      queryFn={apiFetch(
        "documentTemplates",
        "loadStatisticsDocumentTemplates",
        {
          searchKey,
        },
        "Erreur lors du chargement des modèles de documents"
      )}
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
    >
      {(data) => (
        <div className="c1 gap10">
          {data?.map((template) => {
            return (
              <Card
                key={uid()}
                color="green"
                title={template.name}
                onClick={async () => {
                  let patientDocument: PatientDocument = {
                    content: template.content,
                    title: template.title,
                    patient: null as any,
                    creator: getUserId(),
                    refAnswer: null,
                    status: "pending",
                    template,
                  };

                  await openDocumentForm({
                    patient: null,
                    patientDocument,
                  });
                }}
              ></Card>
            );
          })}
        </div>
      )}
    </FetchableList>
  );
}

function FilterCard({
  variableSections,
  filter,
  onChange,
  onClick,
  onDelete,
  level,
}: {
  variableSections: (FieldsSection | null | undefined)[] | null | undefined;
  filter: StatisticsFilter;
  onChange: (filter: StatisticsFilter) => void;
  onClick?: () => void;
  onDelete?: () => void;
  level: number;
}) {
  return (
    <div
      className="card"
      style={{
        background: level == 0 ? "rgba(0, 164, 255, 0.2)" : "",
        boxShadow: level == 0 ? "none" : undefined,
      }}
      onClick={async () => {
        if (filter.table == "refAnswers") {
          let subFilters = filter?.subFilters || [];
          let reference = await apiFetch("references", "loadOne", {
            referenceId: filter.localId,
          });
          if (!reference) return;

          await openDrawer({
            id: "refAnswerDrawer",
            title: "Signe",
            confirmBeforeClose: true,

            content: (
              <VariableSectionsProvider value={{ sections: variableSections }}>
                <RefAnswerFieldsPicker
                  reference={reference}
                  current={filter?.subFilters || []}
                  onChange={(newValue) => {
                    subFilters = newValue;
                  }}
                ></RefAnswerFieldsPicker>
              </VariableSectionsProvider>
            ),
            onConfirm: () => {
              filter.subFilters = subFilters;
              onChange(filter);
            },
          });
          return;
        }

        await onClick?.();
      }}
    >
      <CornerIconButton
        type="remove"
        onClick={() => {
          onDelete?.();
        }}
      ></CornerIconButton>

      <div className="c2 acenter gap5">
        <div>{filter.visibleName || filter.columnName}</div>

        <FilterCardValue statisticsFilter={filter}></FilterCardValue>
      </div>
      <div className="c1 gap5">
        {filter.subFilters.length == 0 && (
          <div className="gray center blueish p5 br">
            Ajouter plus de détails
          </div>
        )}
        {filter.subFilters.map((subFilter, index) => {
          return (
            <>
              {index != 0 ? <div className="gray center">Et</div> : null}
              <FilterCard
                key={uid()}
                variableSections={variableSections}
                level={level + 1}
                filter={subFilter}
                onChange={(newSubFilter) => {
                  filter.subFilters[index] = newSubFilter;
                  onChange(filter);
                }}
                onDelete={() => {
                  filter.subFilters.splice(index, 1);
                  onChange(filter);
                }}
              ></FilterCard>
            </>
          );
        })}
      </div>
    </div>
  );
}

function FilterCardValue({
  statisticsFilter,
}: {
  statisticsFilter: StatisticsFilter;
}) {
  let filter = deepReplaceVariables({
    patientDocument: null,
    isPlaceholder: true,
    value: jsonAbstract(statisticsFilter),
  });
  console.log("filter.value", filter.value);
  let child: any = (
    <div>
      {typeof filter.value == "object"
        ? JSON.stringify(filter.value)
        : filter.value}
    </div>
  );

  if (Array.isArray(filter.value)) child = filter.value.join(" Ou ");
  if (filter.valueType == "boolean")
    child = filter.value == "true" ? "Oui" : "Non";

  if (filter.valueType == "minMaxNumber")
    child = (
      <>
        Entre {JSON.stringify(filter.value.min)} et{" "}
        {JSON.stringify(filter.value.max)}
      </>
    );

  if (filter.valueType == "minMaxDate")
    child = (
      <>
        Entre {JSON.stringify(filter.value.min)} et{" "}
        {JSON.stringify(filter.value.max)}
      </>
    );

  if (Array.isArray(filter.visibleValue)) {
    let children = [];
    for (let part of filter.visibleValue) {
      if (children.length > 0)
        children.push(
          <div className="gray " style={{ fontWeight: "normal" }}>
            Ou
          </div>
        );
      children.push(<div>{part}</div>);
    }
    child = children;
  } else if (filter.visibleValue)
    child = replaceVariablesWithValues({
      content: filter.visibleValue,
      patientDocument: null,
      isPlaceholder: true,
    });

  return <div className="bold tright grid c1 gap5">{child}</div>;
}

//! //////////////////////////////////////////////////////////////////
//! /////////////////     PATIENT IDENTITY   ///////////////////////////
//! /////////////////////////////////////////////////////////////////////

function StatisticsPatientIdentityForm({
  current,
  onChange,
}: {
  current: StatisticsFilter[];
  onChange: (filters: StatisticsFilter[]) => void;
}) {
  let [filters, setFilters] = useState(current || []);

  function getValue(columnName: keyof Patient) {
    let filter = filters.find((filter) => filter.columnName == columnName);
    return filter ? filter.value : null;
  }

  function onInputChange(
    newValue: any,
    columnName: keyof Patient,
    valueType: StatisticsValueType,
    visibleName?: string
  ) {
    let newFilter: StatisticsFilter = {
      table: "patients",
      valueType,
      columnName: columnName,
      visibleName: patientColumnsNames[columnName] || visibleName || "",
      value: newValue,
      subFilters: [],
      creator: getUserId(),
    };

    let currentIndex = filters.findIndex(
      (filter) =>
        filter.table == newFilter.table &&
        filter.columnName == newFilter.columnName
    );

    if (currentIndex != -1) filters[currentIndex] = newFilter;
    else filters.push(newFilter);

    // setFilters([...filters]);
    onChange([...filters]);
  }

  function getCommunes() {
    let selectedWilayas = getValue("wilaya") || [];
    if (!Array.isArray(selectedWilayas)) selectedWilayas = [selectedWilayas];
    let allCommunes: string[] = [];
    for (let wilaya of selectedWilayas) {
      allCommunes = [
        ...allCommunes,
        ...(communes[wilaya as keyof typeof communes] || []),
      ];
    }
    return allCommunes;
  }

  return (
    <div className="grid c1 gap10">
      <StatisticsInput
        name="Date de naissance"
        type="date"
        value={getValue("birth")}
        onChange={(newValue) => {
          onInputChange(newValue, "birth", "minMaxDate");
        }}
      ></StatisticsInput>

      <Input
        name="Âge minimum"
        type="select"
        options={getAgeOptions()}
        value={getValue("minAge")}
        onChange={(newValue) => {
          onInputChange(newValue, "minAge", "select");
        }}
      ></Input>
      <Input
        name="Âge maximum"
        type="select"
        options={getAgeOptions()}
        value={getValue("maxAge")}
        onChange={(newValue) => {
          onInputChange(newValue, "maxAge", "select");
        }}
      ></Input>

      <StatisticsInput
        name="Age en mois (pédiatrie)"
        type="number"
        value={getValue("birth")}
        onChange={(newValue) => {
          onInputChange(newValue, "birth", "minMaxNumber");
        }}
      ></StatisticsInput>
      <StatisticsInput
        name="Calculé à la date du"
        type="text"
        //@ts-ignore
        value={getValue("substractDate")}
        onChange={(newValue) => {
          onInputChange(
            newValue,
            //@ts-ignore
            "substractDate",
            "additional",
            "Naissance calculée à la date du"
          );
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Sexe"
        type="select"
        options={[
          { value: "homme", label: "Masculin" },
          { value: "femme", label: "Féminin" },
        ]}
        value={getValue("sex")}
        onChange={(newValue) => {
          onInputChange(newValue, "sex", "select");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Nationalité"
        type="select"
        value={getValue("nationality")}
        options={nationalities.map((nationality) => {
          return { value: nationality, label: nationality };
        })}
        onChange={(newValue) => {
          onInputChange(newValue, "nationality", "select");
        }}
      ></StatisticsInput>
      <StatisticsInput
        name="Wilaya"
        type="select"
        value={getValue("wilaya")}
        options={wilayas.map((wilaya) => {
          return { value: wilaya, label: wilaya };
        })}
        onChange={(newValue) => {
          onInputChange(newValue, "wilaya", "select");
        }}
      ></StatisticsInput>

      <StatisticsInput
        key={`${getValue("wilaya")} commune`}
        name="Commune"
        type="select"
        value={getValue("commune")}
        options={getCommunes().map((commune) => {
          return { value: commune, label: commune };
        })}
        onChange={(newValue) => {
          onInputChange(newValue, "commune", "select");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Groupage"
        type="select"
        options={[
          { value: "A+", label: "A+" },
          { value: "A-", label: "A-" },
          { value: "B+", label: "B+" },
          { value: "B-", label: "B-" },
          { value: "AB+", label: "AB+" },
          { value: "AB-", label: "AB-" },
          { value: "O+", label: "O+" },
          { value: "O-", label: "O-" },
        ]}
        value={getValue("bloodType")}
        onChange={(newValue) => {
          onInputChange(newValue, "bloodType", "select");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Décès"
        type="select"
        options={[
          {
            value: "vivant",
            label: "Vivant",
          },
          {
            value: "décédé",
            label: "Décédé",
          },
        ]}
        value={getValue("deathStatus")}
        onChange={(newValue) => {
          onInputChange(newValue, "deathStatus", "select");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Date de décès"
        type="date"
        value={getValue("deathDate")}
        onChange={(newValue) => {
          onInputChange(newValue, "deathDate", "minMaxDate");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Nombre d'enfants"
        type="number"
        value={getValue("childrenCount")}
        onChange={(newValue) => {
          onInputChange(newValue, "childrenCount", "minMaxNumber");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Affiliation"
        type="select"
        options={Object.values(assuranceAffiliations).map((affiliation) => {
          return { value: affiliation.id, label: affiliation.name };
        })}
        value={getValue("assuranceAffiliation")}
        onChange={(newValue) => {
          onInputChange(newValue, "assuranceAffiliation", "select");
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Date de création du dossier"
        type="date"
        value={getValue("_date")}
        onChange={(newValue) => {
          onInputChange(newValue, "_date", "minMaxDate");
        }}
      ></StatisticsInput>
    </div>
  );
}

function getAgeOptions() {
  let ageOptions: { value: string; label: string }[] = [];
  for (let i = 0; i < 60; i++) {
    let month = i;
    ageOptions.push({ value: month.toString(), label: `${month} mois` });
  }
  for (let i = 2; i < 100; i++) {
    let month = i * 12;
    ageOptions.push({ value: month.toString(), label: `${i} ans` });
  }
  return ageOptions;
}

//! ///////////////////////////////////////////////////////////////////////////////////////////////////
//! /////////////////     REFERENCES (antecedents, motives, diagnostics)   ///////////////////////////
//! /////////////////////////////////////////////////////////////////////////////////////////////////

function RefAnswerPicker({
  categoriesIds,
  current,
  onChange,
}: {
  categoriesIds: ReferenceCategory[];
  current: StatisticsFilter[];
  onChange: (newValue: StatisticsFilter[]) => void;
}) {
  let [filters, setFilters] = useState(current);
  let [searchKey, setSearchKey] = useState("");

  function onFilterChange(newFilter: StatisticsFilter) {
    let currentIndex = filters.findIndex(
      (filter) =>
        filter.table == newFilter.table &&
        filter.columnName == newFilter.columnName &&
        filter.localId == newFilter.localId
    );

    if (currentIndex != -1) filters[currentIndex] = newFilter;
    else filters.push(newFilter);

    // setFilters([...filters]);
    onChange([...filters]);
  }

  return (
    <FetchableList
      queryKey={["RefAnswerPicker", searchKey, JSON.stringify(categoriesIds)]}
      queryFn={() =>
        apiFetch("references", "loadMany", {
          categoriesIds,

          searchKey: searchKey?.toLowerCase(),
        })
      }
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
    >
      {(data) => (
        <div className="grid c1 gap10">
          {data?.map((reference) => {
            if (reference?.id == "references:1711501339733io98mobx5h")
              return null;
            if (reference?.id == "references:1711752175029o8r4whgic")
              return null;
            let filter = filters.find(
              (filter) =>
                filter.table == "refAnswers" &&
                filter.columnName == "reference.id" &&
                filter.value == reference.id
            );

            return (
              <Card
                key={uid()}
                color={filter ? "blue" : "simple"}
                leading={<div></div>}
                title={
                  <SearchKeyHighliter searchKey={searchKey}>
                    {reference.name}
                  </SearchKeyHighliter>
                }
                subTitle={reference.category}
                trailing={
                  <div className="gray">{reference.usageCount || 0}</div>
                }
                onClick={async () => {
                  onFilterChange({
                    table: "refAnswers",
                    columnName: "reference.id",
                    localId: reference.id,
                    visibleName: reference.name || "Référence",
                    visibleValue: " ",
                    valueType: "select",
                    value: reference.id,
                    subFilters: [],
                    creator: getUserId(),
                  });
                }}
              ></Card>
            );
          })}
        </div>
      )}
    </FetchableList>
  );
}

function RefAnswerFieldsPicker({
  reference,
  current,
  onChange,
}: {
  reference: Reference;
  current: StatisticsFilter[];
  onChange: (newValue: StatisticsFilter[]) => void;
}) {
  let [filters, setFilters] = useState(current);

  function onFilterChange(newFilter: StatisticsFilter) {
    newFilter.visibleValue = deepReplaceVariables({
      isPlaceholder: true,
      patientDocument: null,
      value: newFilter.visibleValue,
    });
    if (!newFilter.value || newFilter.value?.length == 0) {
      filters = filters.filter((filter) => filter.localId != newFilter.localId);
      // setFilters([...filters]);
      onChange([...filters]);
      return;
    }

    let currentIndex = filters.findIndex(
      (filter) =>
        filter.table == newFilter.table &&
        filter.columnName == newFilter.columnName &&
        filter.localId == newFilter.localId
    );

    if (currentIndex != -1) filters[currentIndex] = newFilter;
    else filters.push(newFilter);

    // setFilters([...filters]);
    onChange([...filters]);
  }

  return (
    <div className="c1 gap10">
      {reference?.sections?.map((section) => {
        if (!section) return null;
        return (
          <div key={section.id} className="c1 gap10">
            {reference!.sections!.length > 1 ? (
              <div className="bold f15">{section.name}</div>
            ) : null}
            <div className="grid c1 gap15">
              {section?.fields?.map((field) => {
                if (!field) return null;
                let fieldAnswer = filters.find(
                  (filter) =>
                    filter.table == "fieldAnswers" && filter.localId == field.id
                );

                return (
                  <StatisticsInput
                    key={field.id}
                    type={field.type}
                    value={fieldAnswer?.value || ""}
                    name={`${field.name}`}
                    placeholder={field.placeholder}
                    options={field.options
                      ?.filter((a) => a)
                      .map((option) => {
                        return { value: option!.id, label: option!.name };
                      })}
                    onChange={(newValue, valueType) => {
                      onFilterChange({
                        table: "fieldAnswers",
                        columnName: "value",
                        localId: field.id,
                        valueType,
                        value: newValue,

                        visibleName: field.name,
                        visibleValue:
                          field.options
                            ?.filter(
                              (option) =>
                                option?.name && newValue.includes(option.id)
                            )
                            ?.map((option) => option!.name) || newValue,

                        subFilters: [],
                        creator: getUserId(),
                      });
                    }}
                  ></StatisticsInput>
                );
              })}
            </div>
          </div>
        );
      })}

      <StatisticsInput
        key={"_date"}
        type={"datetime"}
        value={
          filters.find(
            (filter) =>
              filter.table == "fieldAnswers" && filter.localId == "_date"
          )?.value || ""
        }
        name={`Date de création`}
        placeholder={"Date de création"}
        onChange={(newValue, valueType) => {
          onFilterChange({
            table: "refAnswers",
            columnName: "_date",
            localId: "_date",
            visibleName: "Date de création",
            visibleValue: `Du ${newValue.min} au ${newValue.max} `,
            valueType,
            value: newValue,
            subFilters: [],
            creator: getUserId(),
          });
        }}
      ></StatisticsInput>
    </div>
  );
}

//! ///////////////////////////////////////////////////////////////////////////////////////////////////
//! /////////////////     APPOINTMENTS   //////////////////////////////////////////////////////////////
//! /////////////////////////////////////////////////////////////////////////////////////////////////

function StatisticsAppointmentPicker({
  current,
  onChange,
}: {
  current: StatisticsFilter[];
  onChange: (newValue: StatisticsFilter[]) => void;
}) {
  let [filters, setFilters] = useState(current);
  let [searchKey, setSearchKey] = useState("");

  function onFilterChange(newFilter: StatisticsFilter) {
    if (!newFilter.value || newFilter.value?.length == 0) {
      filters = filters.filter(
        (filter) =>
          filter.localId != newFilter.localId &&
          filter.columnName != newFilter.columnName
      );
      // setFilters([...filters]);
      onChange([...filters]);
      return;
    }

    let currentIndex = filters.findIndex(
      (filter) =>
        filter.table == newFilter.table &&
        filter.columnName == newFilter.columnName
    );

    if (currentIndex != -1) filters[currentIndex] = newFilter;
    else filters.push(newFilter);

    // setFilters([...filters]);
    onChange([...filters]);
  }

  return (
    <FetchableList
      queryKey={["StatisticsAppointmentPicker", searchKey]}
      queryFn={() =>
        apiFetch("appointmentTypes", "loadMany", {
          searchKey: searchKey?.toLowerCase(),
        })
      }
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
    >
      {(data) => (
        <div className="grid c1 gap10">
          {data?.map((appointmentType) => {
            let currentAppointment = filters.find(
              (filter) =>
                filter.table == "appointments" &&
                filter.columnName == "type" &&
                filter.value == appointmentType.id
            );

            return (
              <div
                key={uid()}
                className={`card ${currentAppointment ? "active" : ""}`}
                onClick={async () => {
                  await openDrawer({
                    id: "statisticsAppointmentDrawer",
                    title: "Signe",
                    confirmBeforeClose: true,
                    content: (
                      <StatisticsAppointmentForm
                        current={filters}
                        onChange={(newFilters) => {
                          filters = newFilters;
                          setFilters([...filters]);
                        }}
                      ></StatisticsAppointmentForm>
                    ),
                    onConfirm: () => {
                      onFilterChange({
                        table: "appointments",
                        columnName: "type",
                        visibleName: "Type de rendez-vous",
                        visibleValue: appointmentType.name,
                        valueType: "select",
                        value: appointmentType.id,
                        subFilters: [],
                        creator: getUserId(),
                      });
                    },
                  });
                }}
              >
                {appointmentType?.name}
              </div>
            );
          })}
        </div>
      )}
    </FetchableList>
  );
}

function StatisticsAppointmentForm({
  current,
  onChange,
}: {
  current: StatisticsFilter[];
  onChange: (filters: StatisticsFilter[]) => void;
}) {
  let [filters, setFilters] = useState(current);

  function onFilterChange(newFilter: StatisticsFilter) {
    let currentIndex = filters.findIndex(
      (filter) =>
        filter.table == newFilter.table &&
        filter.columnName == newFilter.columnName
    );

    if (currentIndex != -1) filters[currentIndex] = newFilter;
    else filters.push(newFilter);

    // setFilters([...filters]);
    onChange([...filters]);
  }

  function getValue(columnName: string) {
    let filter = filters.find((filter) => filter.columnName == columnName);
    return filter ? filter.value : null;
  }

  return (
    <div className="grid c1 gap10">
      <StatisticsInput
        name="Date du rendez-vous"
        type="date"
        value={getValue("start")}
        onChange={(newValue) => {
          onFilterChange({
            table: "appointments",
            columnName: "start",
            visibleName: "Date",
            valueType: "minMaxDate",
            value: newValue,
            subFilters: [],
            creator: getUserId(),
          });
        }}
      ></StatisticsInput>

      <StatisticsInput
        name="Patient Hospitalisé"
        type="select"
        value={getValue("isHospitalized")}
        options={[
          { value: "true", label: "Oui" },
          { value: "false", label: "Non" },
        ]}
        onChange={(newValue) => {
          onFilterChange({
            table: "appointments",
            columnName: "isHospitalized",
            visibleName: "Hospitalisé",
            valueType: "boolean",
            value: newValue,
            subFilters: [],
            creator: getUserId(),
          });
        }}
      ></StatisticsInput>
      <StatisticsInput
        name="Urgence"
        type="select"
        value={getValue("isUrgent")}
        options={[
          { value: "true", label: "Oui" },
          { value: "false", label: "Non" },
        ]}
        onChange={(newValue) => {
          onFilterChange({
            table: "appointments",
            columnName: "isUrgent",
            visibleName: "Urgent",
            valueType: "boolean",
            value: newValue,
            subFilters: [],
            creator: getUserId(),
          });
        }}
      ></StatisticsInput>
      <StatisticsInput
        name="Statut"
        type="select"
        value={getValue("status")}
        options={Object.values(appointmentStatuses).map((status) => {
          return { value: status.id, label: status.name };
        })}
        onChange={(newValue) => {
          onFilterChange({
            table: "appointments",
            columnName: "status",
            visibleName: "Statut",
            visibleValue: Object.values(appointmentStatuses).find(
              (status) => status.id == newValue
            )?.name,
            valueType: "select",
            value: newValue,
            subFilters: [],
            creator: getUserId(),
          });
        }}
      ></StatisticsInput>
    </div>
  );
}

function StatisticsInput({
  name,
  value,
  type,
  placeholder,
  rows,
  options,
  onChange,
}: {
  name: string;
  value: any;
  type: InputsType;
  rows?: number;
  placeholder?: string;
  options?: { value: string | null | undefined; label: string }[];
  onChange: (newValue: any, valueType: StatisticsValueType) => void;
}) {
  let [localValue, setLocalValue] = useState(value);
  console.log("value", value);
  const [isVariable, setIsVariable] = useState(
    false
    // containsVariable(value) ? true : false
  );

  let variableSectionsContext = useContext(VariableSectionsContext);

  function containsVariable(myValue: any) {
    if (!myValue) return false;
    if (Array.isArray(myValue)) {
      for (let val of myValue) {
        return containsVariable(val);
      }
      return false;
    }
    if (typeof myValue == "object") {
      for (let val of Object.values(myValue)) {
        return containsVariable(val);
      }
      return false;
    }
    return myValue?.toString()?.includes("span") ? true : false;
  }

  function onValueChange(newValue: any, valueType: StatisticsValueType) {
    if (isVariable != true) setLocalValue(newValue);
    onChange?.(newValue, valueType);
  }

  let isEmpty = value == null || value?.length == 0;

  return (
    <div
      className={`field ${isEmpty ? "" : "card"}`}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className={`label ${isEmpty ? "" : "blue-color bold"}`}>
        <div>{name}</div>
        <IconButton
          onClick={() => {
            setIsVariable(!isVariable);
          }}
        >
          <HiOutlineVariable />
        </IconButton>
      </div>

      {(type == "select" ||
        type == "select-multiple" ||
        type == "chips" ||
        type == "chips-multiple") && (
        <MySelect
          options={options || []}
          value={localValue}
          isMultiple={true}
          placeholder={placeholder}
          onChange={(newValue) => {
            onValueChange(newValue, "select");
          }}
        ></MySelect>
      )}

      {(type == "text" || type == "textarea") && !isVariable && (
        <MySelect
          options={options || []}
          value={localValue}
          isMultiple={true}
          isCreatable={true}
          placeholder={"Contient les element suivants..."}
          onChange={(newValue) => {
            onValueChange(newValue, "text");
          }}
        ></MySelect>
      )}

      {(type == "text" || type == "textarea") && isVariable && (
        <VariableInputPicker
          name=""
          value={localValue?.toString()}
          sections={variableSectionsContext?.sections || []}
          toolbarId={name || "variable"}
          onChange={(newValue) => {
            // setLocalValue(newValue);
            onValueChange(newValue, "text");
            // setTimeout(() => {
            //   console.log("newvalue", newValue);
            //   console.log("lcocal", localValue);
            // }, 10);
          }}
        ></VariableInputPicker>
      )}

      {(type == "datetime" || type == "date") && !isVariable && (
        <MinMaxPicker
          value={localValue}
          type={isVariable ? "variable" : type}
          variableSectionsContext={variableSectionsContext}
          placeholder={placeholder}
          onChange={(newValue) => {
            onValueChange(newValue, "minMaxDate");
          }}
        />
      )}
      {(type == "datetime" || type == "date") && isVariable && (
        <VariableMinMaxPicker
          value={localValue}
          variableSectionsContext={variableSectionsContext}
          placeholder={placeholder}
          onChange={(newValue) => {
            onValueChange(newValue, "minMaxDate");
          }}
        />
      )}
      {type == "number" && !isVariable && (
        <MinMaxPicker
          value={localValue}
          type={isVariable ? "variable" : type}
          placeholder={placeholder}
          variableSectionsContext={variableSectionsContext}
          onChange={(newValue) => {
            onValueChange(newValue, "minMaxNumber");
          }}
        />
      )}
      {type == "number" && isVariable && (
        <VariableMinMaxPicker
          value={localValue}
          variableSectionsContext={variableSectionsContext}
          placeholder={placeholder}
          onChange={(newValue) => {
            onValueChange(newValue, "minMaxNumber");
          }}
        />
      )}

      {type == "boolean" && (
        <select
          value={
            value === "true" || value === true || value === 1 || value === "1"
              ? "true"
              : "false"
          }
          onChange={(e) => {
            onChange?.(e.target.value == "true", "boolean");
          }}
        >
          <option value={"true"}>Oui</option>;
          <option value={"false"}>Non</option>;
        </select>
      )}
    </div>
  );
}

export function VariableMinMaxPicker({
  value,
  placeholder,
  variableSectionsContext,
  onChange,
}: {
  value: { min: any; max: any } | null | undefined;
  placeholder?: string;
  variableSectionsContext?: any;
  onChange: (newValue: { min: any; max: any } | null | undefined) => void;
}) {
  let [localValue, setLocalValue] = useState(
    typeof value == "object" ? value : { min: null, max: null }
  );

  function onValueChange(newValue: any, key: "min" | "max") {
    localValue ||= { min: null, max: null };
    localValue[key] = newValue;
    onChange?.(localValue);
  }

  return (
    <div className="c2 gap15">
      <VariableInputPicker
        name="Min"
        value={localValue?.min?.toString() || ""}
        sections={variableSectionsContext?.sections || []}
        toolbarId={`minMaxPicker-${uid()}` || "variable"}
        onChange={(newValue) => {
          onValueChange(newValue, "min");
        }}
      ></VariableInputPicker>
      <VariableInputPicker
        name="Max"
        value={localValue?.max?.toString() || ""}
        sections={variableSectionsContext?.sections || []}
        toolbarId={`minMaxPicker-${uid()}` || "variable"}
        onChange={(newValue) => {
          onValueChange(newValue, "max");
        }}
      ></VariableInputPicker>
    </div>
  );
}

export { FiltersPicker, StatisticsPage };
